import {
  modelCatalogueOf,
  panoVariantsOf,
  servedWorldsOf,
  validateLesson,
  type CueMap,
  type Lesson,
  type ModelCatalogue,
  type PanoVariants,
  type ServedWorld,
  withCurrentWorldNames,
} from '@vrlearning/lesson-schema';
import { catalogueOfServed } from './served-catalogue.js';
import { cuesFrom } from '../engine/guide.js';
import { DeviceRevokedError, deviceFetch, type DeviceIdentity } from './pairing-client.js';
import bundledIndex from 'virtual:lesson-index';

/**
 * Getting lessons, from the API when paired and from the bundle when not.
 *
 * A paired headset asks the API, so a school sees the lessons it is licensed
 * for rather than whatever shipped in the bundle. An unpaired one — a laptop
 * running the desktop preview, or a headset being set up — falls back to the
 * lesson files built into the app, so development never needs a running API.
 *
 * Either way the body is validated before it renders, and either way the
 * service worker has cached it, so a classroom without wifi still works.
 */

export type LessonSummary = {
  readonly id: string;
  readonly title: string;
  readonly language: string;
  readonly mode: string;
  readonly shared: boolean;
  /**
   * Whether a student may start this alone, in the free time a teacher gives
   * them after a lesson. Absent means no.
   */
  readonly selfPractice: boolean;
  /** The world a world lesson takes place in, for the lobby to fetch ahead (ADR-032). */
  readonly world?: string;
  /** Set for API lessons; the bundled ones are fetched by file name instead. */
  readonly version?: number;
  readonly file?: string;
};

export type LoadedLesson = {
  readonly lesson: Lesson;
  /** media id -> signed URL. Empty for a lesson that uses none. */
  readonly media: Readonly<Record<string, string>>;
  /** media id -> the subtitle cues of its speech (ADR-035). Empty without: the voice reads over the text. */
  readonly cues: CueMap;
  /** media id -> a 360° photo's signed cube and light copy (ADR-041). Empty without. */
  readonly variants: PanoVariants;
  /** The library models the lesson names, as the API resolved them (ADR-043). Empty without. */
  readonly models: ModelCatalogue;
  /**
   * The served world packages of the lesson's world, by slug (ADR-049); the headset builds one in
   * place of the code world of that slug. Empty for a bundled lesson or an API without them.
   */
  readonly worlds: Readonly<Record<string, ServedWorld>>;
};

const bundled = (): LessonSummary[] =>
  bundledIndex.map((entry) => ({
    id: entry.id,
    title: entry.title,
    language: entry.language,
    mode: entry.mode,
    shared: true,
    // The bundled lessons are the demo set and the offline fallback. They are
    // not free-time material until somebody says so in the document.
    selfPractice: false,
    file: entry.file,
    ...(entry.world ? { world: entry.world } : {}),
  }));

/**
 * The lessons this headset may play.
 *
 * Falls back to the bundled list on any failure rather than showing an empty
 * lobby: a headset that cannot reach the API can still teach the lessons it
 * already has. Except the API saying the headset was unlinked: it has been
 * unpaired by then, and there is no list to show (DeviceRevokedError).
 */
export async function listLessons(identity: DeviceIdentity | undefined): Promise<LessonSummary[]> {
  if (!identity) return bundled();

  try {
    const response = await deviceFetch(identity, '/v1/lessons');
    if (!response.ok) return bundled();

    const body = (await response.json()) as {
      lessons: {
        id: string;
        title: string;
        language: string;
        mode: string;
        shared: boolean;
        selfPractice?: boolean;
        currentVersion: number;
        world?: string;
      }[];
    };

    // An API that answers with nothing is not a reason to strand a class.
    if (body.lessons.length === 0) return bundled();

    return body.lessons.map((lesson) => ({
      id: lesson.id,
      title: lesson.title,
      language: lesson.language,
      mode: lesson.mode,
      shared: lesson.shared,
      selfPractice: lesson.selfPractice === true,
      version: lesson.currentVersion,
      ...(lesson.world ? { world: lesson.world } : {}),
    }));
  } catch (error) {
    if (error instanceof DeviceRevokedError) throw error;
    return bundled();
  }
}

/** Loads one lesson, validating it before it can reach the renderer. */
export async function loadLesson(
  summary: LessonSummary,
  identity: DeviceIdentity | undefined,
): Promise<LoadedLesson> {
  const fromApi = identity !== undefined && summary.version !== undefined;

  // Unlinked: deviceFetch unpairs the headset and throws DeviceRevokedError.
  const response =
    fromApi && identity
      ? await deviceFetch(identity, `/v1/lessons/${summary.id}/versions/${summary.version}`)
      : await fetch(`/lessons/${summary.file}`);
  if (!response.ok) throw new Error(`kon de les niet laden (${response.status})`);

  const payload: unknown = await response.json();
  // The API wraps the body with its signed media URLs; a bundled file is the
  // body itself.
  const raw = fromApi ? (payload as { lesson: unknown }).lesson : payload;
  const media = fromApi ? ((payload as { media?: Record<string, string> }).media ?? {}) : {};
  const cues = fromApi ? cuesFrom((payload as { cues?: unknown }).cues) : {};
  const variants = fromApi ? panoVariantsOf((payload as { variants?: unknown }).variants) : {};
  const models = fromApi ? modelCatalogueOf((payload as { models?: unknown }).models) : {};
  const worlds = fromApi ? servedWorldsOf((payload as { worlds?: unknown }).worlds) : {};

  // The same validator the API runs. A lesson that does not validate must not
  // reach a class: better a clear message than a silent blank panel.
  // Judged by the worlds this lesson came with (a package's features too); the headset never refuses
  // a world it cannot build -- it shows the studio.
  const catalogue = catalogueOfServed(worlds);
  const result = validateLesson(raw, { worlds: catalogue });
  if (!result.valid) {
    const first = result.errors[0];
    throw new Error(`les is ongeldig: ${first?.path ?? ''} ${first?.message ?? ''}`);
  }

  // A world's old names played as its current ones (spec 2026-10-16).
  const lesson = withCurrentWorldNames(result.lesson, catalogue);
  return { lesson, media, cues, variants, models, worlds };
}
